import { ApiError } from '@/shared/api/api-client';
import { refusalMessage } from './decision-identity';

// PROD-3181 — the shapes of the Scrips.Patient ambient recording endpoints (PROD-2702,
// API/Controllers/AmbientRecordingsController.cs, Application/Dtos/PatientChart/AmbientRecordingDtos.cs):
//
//   GET    api/Patients/{patientId}/Encounters/{encounterId}/AmbientRecordings
//   POST   api/Patients/{patientId}/Encounters/{encounterId}/AmbientRecordings
//   DELETE api/Patients/{patientId}/Encounters/{encounterId}/AmbientRecordings/{recordingId}
//   POST   api/Patients/{patientId}/Encounters/{encounterId}/AmbientRecordings/{recordingId}/Download
//
// Every refusal answers `{ error, code }`; `code` is a stable value this app branches on.

/** pending | active | ended | superseded | deleted (`removing` is a moment inside a replace or delete). */
export type AmbientRecordingStatus = 'pending' | 'removing' | 'active' | 'ended' | 'superseded' | 'deleted';

export type ReRecordChoice = 'keep-both' | 'replace';

export interface AmbientRecording {
  id: string;
  recordingKey: string;
  status: AmbientRecordingStatus | string;
  startedAt: string;
  endedAt?: string | null;
  durationMs?: number | null;
  bytes?: number | null;
  startedBy: string;
  reRecordChoice?: ReRecordChoice | null;
  partial: boolean;
  degraded: boolean;
  endedByConsentRevocation: boolean;
  partCount: number;
  audioAvailable: boolean;
  acceptedItemCount: number;
  hasAcceptedItems: boolean;
  /** Replace and delete are allowed by state; the caller's role is checked on the call. */
  canReplaceOrDelete: boolean;
  /** Why not, when `canReplaceOrDelete` is false: an error code. */
  replaceBlockedReason?: string | null;
  supersededByRecordingId?: string | null;
}

export interface AmbientRecordingList {
  encounterSigned: boolean;
  maxPerEncounter: number;
  liveCount: number;
  reRecordChoiceRequired: boolean;
  /** Oldest first. */
  recordings: AmbientRecording[];
}

export interface StartAmbientRecordingBody {
  /** 1-32 characters of [A-Za-z0-9_-]. The same key goes in the socket's context frame. */
  recordingKey: string;
  choice?: ReRecordChoice;
  replaceRecordingId?: string;
}

export interface StartAmbientRecordingResult {
  recording: AmbientRecording;
  replaced?: AmbientRecording | null;
}

export interface AmbientRecordingDownloadPart {
  partIndex: number;
  /** A short-lived read link. A credential: never log it or store it. */
  url: string;
  fileName: string;
  bytes?: number | null;
  durationMs?: number | null;
}

export interface AmbientRecordingDownload {
  recordingId: string;
  expiresAt: string;
  parts: AmbientRecordingDownloadPart[];
}

/**
 * What the panel needs from the recording endpoints. `undefined` until the patient and the
 * encounter are both known; the panel then starts recordings the way it did before PROD-3181.
 */
export interface AmbientRecordingsPort {
  list: AmbientRecordingList | undefined;
  readError: string | null;
  /** The user holds `Ambient_Recording_Download`. The server checks it again on the call. */
  canDownload: boolean;
  /** Reads the list again and answers with it. */
  refresh: () => Promise<AmbientRecordingList | undefined>;
  start: (body: StartAmbientRecordingBody) => Promise<StartAmbientRecordingResult>;
  remove: (recordingId: string) => Promise<AmbientRecording>;
  download: (recordingId: string) => Promise<AmbientRecordingDownload>;
}

/** The permission key Identity grants for downloading recordings. */
export const AMBIENT_RECORDING_DOWNLOAD_PERMISSION = 'Ambient_Recording_Download';

export const RERECORD_CHOICE_REQUIRED = 'rerecord-choice-required';
export const STORE_UNAVAILABLE = 'store-unavailable';

/** The server's stable refusal code, or null (a network failure, or a body without one). */
export function recordingErrorCode(error: unknown): string | null {
  if (!(error instanceof ApiError)) return null;
  try {
    const parsed: unknown = JSON.parse(error.body);
    if (parsed && typeof parsed === 'object' && typeof (parsed as { code?: unknown }).code === 'string') {
      return (parsed as { code: string }).code;
    }
  } catch {
    // Not JSON: no code.
  }
  return null;
}

const MESSAGES: Record<string, string> = {
  'encounter-signed': 'This encounter is signed. Its recordings can no longer change.',
  'recording-has-accepted-items':
    'Items from this recording were accepted, so it must be kept. Choose Keep both instead.',
  'recording-in-progress': 'This recording is still running. Stop it first.',
  'recording-already-removed': 'This recording was already removed.',
  'recording-key-in-use': 'This recording could not be started. Try again.',
  'recording-limit': 'This encounter has the most recordings allowed. Delete one, or replace one.',
  'recording-change-not-permitted':
    'Only the clinician who made this recording, or a physician, can replace or delete it.',
  'recording-not-found': 'This recording was not found on this encounter.',
  'audio-unavailable': 'No saved audio for this recording.',
  'store-unavailable': 'Recordings are not available in this environment.',
  'container-not-private': 'Downloads are turned off until the audio storage is private.',
  'invalid-request': 'The request was not valid. Nothing changed.',
};

/** Plain words for a refusal. A failure that never reached the server says so. */
export function recordingErrorMessage(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Could not reach the server. Nothing changed. Try again.';
  const code = recordingErrorCode(error);
  if (code && MESSAGES[code]) return MESSAGES[code];
  if (error.status === 403) return 'You do not have permission to do this.';
  return refusalMessage(error);
}

/** A recording that still holds audio and counts toward the limit. */
export const isLiveRecording = (r: AmbientRecording): boolean =>
  r.status === 'pending' || r.status === 'active' || r.status === 'ended';
